import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { NavLink, Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { FaInstagram, FaFacebookF } from "react-icons/fa6";

const links = [
  { label: "Home", to: "/" },
  { label: "Origin Story", to: "/biography" },
  { label: "The Soundprint", to: "/discography" },
  { label: "Bragging Rights", to: "/awards" },
  { label: "Iconography", to: "/gallery" },
  { label: "Press", to: "/press" },
  { label: "Stage Zero", to: "/stage-zero" },
  { label: "Events", to: "/the-weekend" },
];

const socials = [
  { label: "Instagram", href: "https://www.instagram.com/timbaland/", Icon: FaInstagram },
  { label: "Facebook", href: "https://www.facebook.com/timbaland", Icon: FaFacebookF },
];

export default function Header({ solid = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const opaque = solid || scrolled || open;

  return (
    <>
      <motion.header
        data-testid="site-header"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          opaque ? "bg-black/80 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1600px] mx-auto flex items-center gap-4 px-4 md:px-10 h-[76px]">
          <Link to="/" data-testid="logo-link" className="flex items-center shrink-0" onClick={() => setOpen(false)}>
            <img src="/timbo-green.png" alt="Timbo Da King" className="h-12 md:h-16 w-auto object-contain" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex flex-1 items-center justify-end gap-x-4 lg:gap-x-6 overflow-x-auto no-scrollbar">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `link-underline font-display uppercase text-[0.66rem] md:text-[0.72rem] tracking-[0.14em] whitespace-nowrap transition-colors ${
                    isActive ? "text-gold" : "text-white/70 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          {/* Desktop socials */}
          <div className="hidden md:flex items-center gap-2 pl-3 ml-1 border-l border-white/10">
            {socials.map(({ label, href, Icon }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                data-testid={`header-social-${label.toLowerCase()}`}
                className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-black hover:bg-gold hover:border-gold transition-all"
              >
                <Icon size={15} />
              </a>
            ))}
          </div>

          {/* Mobile socials + menu button */}
          <div className="md:hidden ml-auto flex items-center gap-2">
            {socials.map(({ label, href, Icon }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                data-testid={`header-social-mobile-${label.toLowerCase()}`}
                className="w-9 h-9 rounded-full border border-gold/40 flex items-center justify-center text-gold"
              >
                <Icon size={15} />
              </a>
            ))}
            <button
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-button"
              aria-label="Open menu"
              className="flex items-center gap-2 border border-gold/50 text-gold rounded-full px-4 py-2 font-display uppercase tracking-[0.2em] text-[0.66rem]"
            >
              <Menu size={16} /> Menu
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile overlay */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-obsidian/98 backdrop-blur-xl md:hidden flex flex-col"
            data-testid="mobile-menu-overlay"
          >
            <div className="flex items-center justify-between px-4 h-[76px] border-b border-white/10">
              <img src="/timbo-green.png" alt="Timbo Da King" className="h-12 w-auto object-contain" />
              <button
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-close"
                aria-label="Close menu"
                className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center text-white"
              >
                <X size={20} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-1">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i + 0.1, ease: [0.16, 1, 0.3, 1] }}
                >
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className={({ isActive }) =>
                      `block font-display uppercase tracking-[0.08em] text-3xl py-3 border-b border-white/8 transition-colors ${
                        isActive ? "text-gold" : "text-white/80"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
